import sizes from '@polyai/ui/styles/config/sizes';
/**
 * AgentWizard Constants
 *
 * Centralized constants for the AgentWizard flow to avoid magic numbers and strings
 */

// Voice variants
export const VOICE_VARIANTS = {
  CEDAR: 'cedar',
  MARIN: 'marin',
} as const;

export type VoiceVariant = (typeof VOICE_VARIANTS)[keyof typeof VOICE_VARIANTS];

// LocalStorage keys (prefixed for easy cleanup)
export const LOCAL_STORAGE_KEYS = {
  COMPANY_URL: 'agent-wizard/company-url',
  SELECTED_VOICE: 'agent-wizard/selected-voice',
  FAQS: 'agent-wizard/faqs',
  SCENARIOS: 'agent-wizard/scenarios',
} as const;

// Legacy key for backwards compatibility
export const SESSION_STORAGE_KEYS = LOCAL_STORAGE_KEYS;

// UI Dimensions
export const UI_DIMENSIONS = {
  BUTTON_WIDTH: 420,
} as const;

// Timing constants
export const TIMING = {
  LOADING_STEP_INTERVAL: 8000, // 8 seconds between loading messages
  PENDO_GUIDE_INITIAL_DELAY: 1000,
  PENDO_GUIDE_CONTAINER_DELAY: 500,
  PENDO_POLLING_INTERVAL: 500,
  PENDO_POLLING_TIMEOUT: 15000,
  BRANCH_MERGE_DELAY: 1000,
  SIGNUP_SIMULATION_DELAY: 1000,
} as const;

// Pendo configuration
export const PENDO_CONFIG = {
  SURVEY_GUIDE_ID: 'SWlUxPNnrWFFvdaqCpf9lgJAXPw',
  SURVEY_STEP_ID: 'O9tEbrFPlh4qEPJOsbgzmqtGXz4',
  EMBED_TARGET_ID: '_pendo-guide-container_',
  MIN_IFRAME_HEIGHT: '400px',
} as const;

// Pendo events
export const PENDO_EVENTS = {
  SURVEY_LOADED: 'agent-wizard-survey-loaded',
  SURVEY_COMPLETED: 'agent-wizard-survey-completed',
} as const;

// Asset paths
export const ASSETS = {
  LOGIN_BG: '/assets/opentable/images/login.webp',
  LOGO_WHITE: '/assets/svg/companyLogos/polyai-logo-white-rgb.svg',
  LOGO_SLATE: '/assets/svg/companyLogos/polyai-logo-slate-rgb.svg',
} as const;

// Blur placeholder for LOGIN_BG (10px wide base64 for instant loading)
export const LOGIN_BG_BLUR_DATA_URL =
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAoAAAANCAYAAACQN/8FAAAAAXNSR0IArs4c6QAAAERlWElmTU0AKgAAAAgAAYdpAAQAAAABAAAAGgAAAAAAA6ABAAMAAAABAAEAAKACAAQAAAABAAAACqADAAQAAAABAAAADQAAAACJWGTlAAABIElEQVQoFU2RXWuDQBREZ1cTLBKSxlSpAcmr+P9/VCCKsWr83t7ZsKUL+iBnz9y5quPxaIIgwOFwQBzHePUv1M8aXddhHEcsywIe38hrXVcMw4C+63G5RFi3FfM820cpBWMMtNk2zHJrmiY0Pw0mAdLvFGEYYr/fw/M8a9SbgLzhrFVVIfgIEEURfN8HjTya0CbRnIXmtm3xeDwQf8XY7XZWQtAnaGGOIPG1RDdNg/PnGS6NoJLGxuolwpkJ8JxOJwuzmOYHGmljIQexjIvmmO8ZxeAA2pMkQZZlFnyXUdDs5JrRfLvdkOe5XQs3wW88Wmn9VyZNUxRFYRfN9pzNJfmkeYvLvV6vuN/vdj0OdEYLQuxahijLEotY+Dtp+x/9CzqJ2itQqRGCAAAAAElFTkSuQmCC';

// Language codes
export const LANGUAGE_CODES = {
  EN_US: 'en-US',
} as const;

// Wizard steps (0-indexed)
export const WIZARD_STEPS = {
  SIGNUP: 0,
  COMPANY: 1,
  SURVEY: 2,
  VOICE: 3,
  AGENT: 4,
} as const;

// Error messages
export const ERROR_MESSAGES = {
  REQUIRED_FIELD: 'Required field',
  FILL_ALL_FIELDS: 'Please fill in all fields and accept the terms',
  NO_AUTH_TOKEN: 'No authentication token available',
  SIGNUP_ERROR: 'An error occurred during signup',
} as const;

// Cache settings
export const CACHE = {
  AGENT_SETUP_EXPIRY_MS: 30 * 60 * 1000, // 30 minutes
} as const;

// Retry settings
export const RETRY_CONFIG = {
  MAX_RETRIES: 3,
  INITIAL_BACKOFF_MS: 1000,
  MAX_BACKOFF_MS: 10000,
} as const;

// Polling settings for async agent setup
export const POLLING_CONFIG = {
  INTERVAL_MS: 10000, // 10 seconds between polls
  MAX_ATTEMPTS: 60, // 10 minutes max (60 * 10s = 600s = 10min)
} as const;

export const WIZARD_CONTENT_WIDTH = sizes.wizardContentWidth;
